































































































































import { namespace } from 'vuex-class';
import { Route } from 'vue-router';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { sendLog } from '@/api/client-logs';
import ApiCancelError from '@/resources/error/ApiCancelError';
import ApiModalError from '@/resources/error/ApiModalError';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';
import PhotoType from '@/resources/enum/PhotoType';
import IdDocType from '@/resources/enum/IdDocType';
import ColorButton from '@/components/ColorButton.vue';
import CameraPageShootCardAuto from '@/components/CameraPageShootCardAuto.vue';
import CameraPageShootCardFrontBack from '@/components/CameraPageShootCardFrontBack.vue';
import CameraPageShootCardDiagonal from '@/components/CameraPageShootCardDiagonal.vue';
import CameraPageShootFace from '@/components/CameraPageShootFace.vue';
import CameraPageHeader from '@/components/CameraPageHeader.vue';
import CameraPageShootButton from '@/components/CameraPageShootButton.vue';
import CameraPageChangeCameraModeButton from '@/components/CameraPageChangeCameraModeButton.vue';
import CameraPageChangeCameraModeMessage from '@/components/CameraPageChangeCameraModeMessage.vue';
import CameraPageChangeCameraModeModal from '@/components/CameraPageChangeCameraModeModal.vue';
import CameraPageShootCheck from '@/components/CameraPageShootCheck.vue';
import MnDocType from '@/resources/enum/MnDocType';
import { CameraDevice, FacingMode } from '@/store/modules/camera';

const cameraModule = namespace('camera');

@Component({
  components: {
    ColorButton,
    CameraPageShootCardAuto,
    CameraPageShootCardFrontBack,
    CameraPageShootCardDiagonal,
    CameraPageShootFace,
    CameraPageHeader,
    CameraPageShootButton,
    CameraPageChangeCameraModeButton,
    CameraPageChangeCameraModeMessage,
    CameraPageShootCheck,
    CameraPageChangeCameraModeModal
  }
})
export default class CameraChildShooting extends Vue {
  private refreshTimerId: number = -1;
  private isShootable: boolean = false;
  private showCameraModeMessage: boolean = true;

  @cameraModule.State('idDocType') idDocType!: IdDocType;

  @Watch('isResize')
  handleShoot(value: boolean) {
    if (value) {
      this.isShootable = false;
    } else {
      this.getPosition();
    }
  }

  private get pageTitle(): string {
    if (this.isGpDocPage) {
      const page: number = this.$store.getters['camera/gpdocPageCurr'];
      return this.$t(this.$store.state.camera.i18nKeyShootTitleString, { page }) as string;
    } else if (this.isOpDocPage) {
      const term: number = this.$store.getters['camera/opdocTermCurr'];
      return this.$t(this.$store.state.camera.i18nKeyShootTitleString, { term }) as string;
    } else {
      return this.$t(this.$store.state.camera.i18nKeyShootTitleString, {
        documentName: this.$store.state.camera.documentName
      }) as string;
    }
  }

  private get isResize(): boolean {
    return this.$store.state.camera.isResize;
  }

  private get isIdDocFront(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-front';
  }

  private get isIdDocDiagonal(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-diagonal';
  }

  private get isIdDocBack(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-back';
  }

  private get isMnDocFront(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-front';
  }

  private get isMnDocBack(): boolean {
    return this.$store.state.camera.shootType === 'mynumber-document-back';
  }

  private get isFaceFront(): boolean {
    return this.$store.state.camera.shootType === 'face-front';
  }

  private get canChangeCameraModeManually(): boolean {
    return this.$store.getters['camera/canChangeCameraModeManually'];
  }

  private get showCameraModeModal(): boolean {
    return this.$store.state.camera.showCameraModeModal;
  }

  private get isIdDocPassport(): boolean {
    if (this.$store.state.camera.idDocType === IdDocType.PassportHo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportTo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportTi) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportAnyIssuanceDateTo) return true;
    if (this.$store.state.camera.idDocType === IdDocType.PassportAnyIssuanceDateTi) return true;
    return false;
  }

  private get isMnDocResidenceCertificate(): boolean {
    return this.$store.state.camera.mnDocType === MnDocType.ResidenceCertificate;
  }

  private get isGpDocPage(): boolean {
    return this.$store.getters['camera/isGpdoc'];
  }

  private get isOpDocPage(): boolean {
    return this.$store.getters['camera/isOpdoc'];
  }

  private get isGeneralBook(): boolean {
    return this.$store.getters['camera/isGeneralBook'];
  }

  private get isGlobal(): boolean {
    return this.$store.getters['camera/isGlobal'];
  }

  // 手動撮影で撮影済みの状態 手動撮影ではtriggerShootがtrueになったらあとはページ遷移をするので、撮影済みと同義である
  private get isManualTriggered(): boolean {
    return !this.$store.state.camera.isDetectStart && this.$store.state.camera.triggerShoot;
  }

  private get cardWidth(): number {
    return this.$store.state.camera.clippingDisplayWidth * 0.9;
  }

  private get cardHeight(): number {
    return this.cardWidth * this.$store.state.camera.idDocAspectRatioView;
  }

  private get isCameraChanging(): boolean {
    return this.$store.state.camera.isCameraChanging;
  }

  private get cameraModeIndex(): number {
    return (
      this.$store.state.camera.selectedDeviceIndex[this.isFaceFront ? FacingMode.User : FacingMode.Environment] + 1
    );
  }

  private get showPageContentsTitle(): boolean {
    if (this.isGlobal) {
      // 海外対応のパスポート以外で表示する
      return this.idDocType !== IdDocType.PassportGlobalNoFace;
    }

    return false;
  }

  public async created(): Promise<void> {
    // 正常撮影開始フラグの確認
    const isStart: boolean = this.$store.state.camera.isStart;
    // 正常撮影開始フラグを戻す
    this.$store.commit('camera/isStart', false);
    // 撮影トリガーを戻す
    this.$store.commit('camera/triggerShoot', false);
    // ページマウント時の設定
    await this.$store.dispatch('camera/mountedSetting', this.$route.path);
    // 正常撮影開始フラグを確認して説明ページからの正しい遷移以外は説明ページに戻す
    if (!isStart && !this.isGpDocPage && !this.isOpDocPage)
      this.$router.replace(this.$store.getters['camera/locationIntroduction']);
  }

  public async mounted(): Promise<void> {
    // ログの送信
    this.sendLogEnter();

    // 撮影情報を取得して撮影を開始する
    this.getPosition();

    this.setRefreshTimer();
  }

  public beforeDestroy(): void {
    window.clearTimeout(this.refreshTimerId);
  }

  // 一定期間以上経過後の処理
  private setRefreshTimer(): void {
    const refreshSecond: number = this.$store.state.camera.isDetectStart ? 30 : 3 * 60;
    this.refreshTimerId = window.setTimeout(this.handleRefresh, refreshSecond * 1000);
  }

  // 撮影ページに入ったログの送信
  private async sendLogEnter(): Promise<void> {
    let logKind: string = '';
    //if (this.isIdDocFront) logKind = 'page_camera_shooting_identity_front';
    //if (this.isIdDocDiagonal) logKind = 'page_camera_shooting_identity_diagonal';
    //if (this.isIdDocBack) logKind = 'page_camera_shooting_identity_back';
    if (this.isMnDocFront) logKind = 'page_camera_shooting_mynumber_front';
    if (this.isMnDocBack) logKind = 'page_camera_shooting_mynumber_back';
    if (this.isGpDocPage) logKind = 'page_camera_shooting_general_page';
    if (this.isOpDocPage) logKind = 'page_camera_shooting_optional_page';
    if (this.isFaceFront) logKind = 'page_camera_shooting_face_front';
    if (logKind === '') return;
    await sendLog({ level: 'INFO', kind: logKind });
  }

  // 撮影情報を取得して撮影を開始する
  private async getPosition(): Promise<void> {
    this.isShootable = false;

    // 撮影処理中だったら撮影開始通信を行わない
    if (this.$store.state.camera.isShooting) return;

    // ランダム枠を取り直す前に自動撮影のカウントをリセットする
    this.$store.commit('camera/resetDetectCount');

    try {
      // 撮影開始通信 ランダム枠の位置を取得する
      await this.$store.dispatch('camera/storeStart');
      // ランダム枠位置の取得に成功したら撮影許可
      this.isShootable = true;
    } catch (error) {
      if (error instanceof ApiCancelError) {
        // キャンセルは握りつぶす
      } else if (error instanceof ApiModalError) {
        // clientRetryableで処理したエラーは握りつぶす
      } else throw error;
    }
  }

  // 一定時間経過後の処理
  private async handleRefresh(): Promise<void> {
    if (this.$store.state.camera.isShooting) {
      // 撮影処理中だったらもう少しだけ待つ
      this.refreshTimerId = window.setTimeout(this.handleRefresh, 100);
    } else if (this.$store.state.camera.isDetectStart && this.$store.state.camera.detectCount > 0) {
      // 自動撮影でうまくいきそうだったらもう少しだけ待つ
      this.refreshTimerId = window.setTimeout(this.handleRefresh, 100);
    } else if (this.$store.state.camera.isDetectStart) {
      // 自動撮影において一定時間経過したら手動撮影への移行を伝えるエラーページへ遷移する
      const fields: string[] = [CameraChildErrorCode.AutodetectTimeout];
      this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
    } else if (this.isIdDocFront || this.isIdDocBack) {
      // 本人確認書類の表面撮影か裏面撮影において一定時間経過したらエラーページへ遷移する
      const fields: string[] = [CameraChildErrorCode.ShootTimeout];
      this.$router.push(Object.assign({}, this.$store.getters['camera/locationError'], { query: { fields } }));
    }
  }

  private handleRequestChangeMode(): void {
    this.$store.dispatch('camera/useNextCameraMode', this.isFaceFront ? FacingMode.User : FacingMode.Environment);
  }

  private handleClose(): void {
    this.showCameraModeMessage = false;
  }

  private handleRequestReboot(): void {
    this.$store.commit('camera/setIsRefreshRequesting', true);

    // refresh処理のタイマーを一度リセットする
    window.clearTimeout(this.refreshTimerId);
    this.$store.commit('camera/setShowCameraModeModal', false);
    this.setRefreshTimer();
  }

  // 撮影する
  private onDetect(): void {
    this.$store.commit('camera/triggerDetect');
  }

  // 撮影する
  private onShoot(): void {
    this.$store.commit('camera/triggerShoot', true);
  }

  private onSkip(): void {
    this.$router.push(this.$store.getters['camera/locationSkip']);
  }
}
